import { abstractdescriptions as abstractDescriptionsAbstract } from '../../Projects/Config/wallcoveringabstractdata';
import { abstractdescriptions as abstractDescriptionsCorporate } from '../../Projects/Config/wallcoveringcorporatedata';
import { naturedescriptions as abstractDescriptionsDecorative } from '../../Projects/Config/wallcoveringnaturedata';
import { kidsdescriptions as abstractDescriptionsKids } from '../../Projects/Config/wallcoveringkidsdata';
import { minimalpatterndescriptions as abstractDescriptionsMinimalPattern } from '../../Projects/Config/wallcoveringminimalandpatterndata';

// Context arrays for each category
const abstractContextAbstract = Array(1000).fill("Abstract wallpapers bring depth and character to any space, sparking creativity, evoking emotions, and adding a unique visual identity through bold forms and imaginative expression.");
const abstractContextCorporate = Array(1000).fill("Wallpapers elevate professional environments by reinforcing brand identity, boosting employee morale, and creating a lasting impression on clients through thoughtful and impactful design.");
const abstractContextDecorative = Array(1000).fill("Decorative wallpapers enhance interiors with charm and elegance, transforming plain walls into expressive features that reflect personality, style, and a refined sense of aesthetics.");
const abstractContextKids = Array(1000).fill("Kids wallpapers bring fun and imagination to interiors, turning ordinary walls into playful spaces full of color and creativity. They create an environment that reflects joy, curiosity, and a lively sense of wonder, making every room feel cheerful and inspiring for children.");
const abstractContextMinimalPattern = Array(1000).fill("Minimal and pattern wallpapers bring timeless sophistication, balance, and modern elegance to interior spaces. With subtle geometries, clean lines, and refined textures, they transform walls into harmonious backdrops that elevate any contemporary room.");

const AWS_BASE_URLS = {
  Abstract: "https://vizaura.s3.eu-north-1.amazonaws.com/WallCoverings/Abstract",
  Corporate: "https://vizaura.s3.eu-north-1.amazonaws.com/WallCoverings/Corporate",
  Decorative: "https://vizaura.s3.eu-north-1.amazonaws.com/WallCoverings/Decorative",
  Kids: "https://vizaura.s3.eu-north-1.amazonaws.com/WallCoverings/Kids",
  MinimalPattern: "https://vizaura.s3.eu-north-1.amazonaws.com/WallCoverings/Minimal%26Pattern"
};

const getImageUrl = (category, imageNumber, useParentheses = false, isKids = false) => {
  const baseUrl = AWS_BASE_URLS[category];
  let formattedNum;
  
  if (isKids) {
    formattedNum = `kids-${imageNumber}`;
  } else if (useParentheses) {
    formattedNum = `(${imageNumber})`;
  } else {
    formattedNum = `${imageNumber}`;
  }
  
  return {
    jpg: `${baseUrl}/${formattedNum}.jpg`,
    jpeg: `${baseUrl}/${formattedNum}.jpeg`
  };
};

function mulberry32(seed) {
  return function () {
    let t = (seed += 0x6D2B79F5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function seededShuffle(array, seed = 123456) {
  const rng = mulberry32(seed);
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

const totalAbstract = 231;
const totalCorporate = 276;
const totalDecorativeOriginal = 545;
const totalDecorativeNew = 43;
const totalKids = 112;
const totalMinimalPattern = 65;

// 1. Abstract Set
const abstractSet = Array.from({ length: totalAbstract }, (_, index) => {
  const imgNum = index + 100; // Start from 100, end at 330
  const imageUrls = getImageUrl('Abstract', imgNum, false);
  return {
    Img: imageUrls.jpeg,
    ImgJpg: imageUrls.jpg,
    ImgJpeg: imageUrls.jpeg, 
    title: `#AW${String(imgNum).padStart(3, '0')}`,
    desc: abstractDescriptionsAbstract[index] || "Default description",
    context: abstractContextAbstract[index] || "Default context",
    btn: 'View Detail',
  };
});

// 2. Corporate Set
const corporateSet = Array.from({ length: totalCorporate }, (_, index) => {
  const imgNum = index + 1;
  const imageUrls = getImageUrl('Corporate', imgNum, true);
  return {
    Img: imageUrls.jpeg,
    ImgJpg: imageUrls.jpg,
    ImgJpeg: imageUrls.jpeg,
    title: `#CR${String(imgNum).padStart(3, '0')}`,
    desc: abstractDescriptionsCorporate[index] || "Default description",
    context: abstractContextCorporate[index] || "Default context",
    btn: 'View Detail',
  };
});

// 3. Decorative Set (Original 545 excluding 325 + New 43 with parentheses)
const decorativeOriginalSet = Array.from({ length: totalDecorativeOriginal }, (_, index) => {
  const imgNum = index + 1;
  
  // Skip 325
  if (imgNum === 325) return null;
  
  const imageUrls = getImageUrl('Decorative', imgNum, false);
  return {
    Img: imageUrls.jpeg,
    ImgJpg: imageUrls.jpg,
    ImgJpeg: imageUrls.jpeg,
    title: `#DC${String(imgNum).padStart(3, '0')}`,
    desc: abstractDescriptionsDecorative[index] || "Default description",
    context: abstractContextDecorative[index] || "Default context",
    btn: 'View Detail',
    number: imgNum,
  };
}).filter(Boolean);

const decorativeNewSet = Array.from({ length: totalDecorativeNew }, (_, index) => {
  const imgNum = index + 1;
  const titleNum = 545 + imgNum;
  const imageUrls = getImageUrl('Decorative', imgNum, true);
  return {
    Img: imageUrls.jpg,
    ImgJpg: imageUrls.jpg,
    ImgJpeg: imageUrls.jpeg,
    title: `#DC${String(titleNum).padStart(3, '0')}`,
    desc: abstractDescriptionsDecorative[545 + index] || "Default description",
    context: abstractContextDecorative[545 + index] || "Default context",
    btn: 'View Detail',
    number: titleNum,
  };
});

const decorativeSet = [...decorativeOriginalSet, ...decorativeNewSet];

// 4. Kids Set
const kidsSet = Array.from({ length: totalKids }, (_, index) => {
  const imgNum = index + 1;
  const imageUrls = getImageUrl('Kids', imgNum, false, true);
  return {
    Img: imageUrls.jpeg,
    ImgJpg: imageUrls.jpg,
    ImgJpeg: imageUrls.jpeg,
    title: `#KD${String(imgNum).padStart(3, '0')}`,
    desc: abstractDescriptionsKids[index] || "Default description",
    context: abstractContextKids[index] || "Default context",
    btn: 'View Detail',
  };
});

// 5. Minimal and Pattern Set
const minimalPatternSet = Array.from({ length: totalMinimalPattern }, (_, index) => {
  const imgNum = index + 1;
  const imageUrls = getImageUrl('MinimalPattern', imgNum, true);
  return {
    Img: imageUrls.jpg,
    ImgJpg: imageUrls.jpg,
    ImgJpeg: imageUrls.jpeg,
    title: `#MP${String(imgNum).padStart(3, '0')}`,
    desc: abstractDescriptionsMinimalPattern[index] || "Default description",
    context: abstractContextMinimalPattern[index] || "Default context",
    btn: 'View Detail',
  };
});

const combinedData = [...abstractSet, ...corporateSet, ...decorativeSet, ...kidsSet, ...minimalPatternSet];

// Function to prioritize specific image (DC242)
function prioritizeImage(data, targetTitle) {
  const targetIndex = data.findIndex(item => item.title === targetTitle);
  if (targetIndex === -1) return seededShuffle(data, 42);
  
  const targetItem = data[targetIndex];
  const remainingData = data.filter((_, index) => index !== targetIndex);
  
  // Shuffle remaining data
  const shuffledRemaining = seededShuffle(remainingData, 42);
  
  // Return target item first, then shuffled remaining
  return [targetItem, ...shuffledRemaining];
}

export const walldata = prioritizeImage(combinedData, '#DC242');
